import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext();

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5005';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(localStorage.getItem('fracadapt-token'));
  const [refreshToken, setRefreshToken] = useState(localStorage.getItem('fracadapt-refresh'));
  const [loading, setLoading] = useState(true);

  const isAuthenticated = !!accessToken && !!user;

  // Get auth headers for API calls
  const getAuthHeaders = useCallback(() => {
    const headers = {};
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }
    // Also include API key for backward compatibility
    const apiKey = process.env.REACT_APP_API_KEY;
    if (apiKey) {
      headers['X-API-Key'] = apiKey;
    }
    return headers;
  }, [accessToken]);

  // Logout
  const logout = useCallback(() => {
    // Try to call logout endpoint (fire and forget)
    if (accessToken) {
      fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessToken}` },
        body: JSON.stringify({ accessToken, refreshToken }),
      }).catch(() => {});
    }
    setUser(null);
    setAccessToken(null);
    setRefreshToken(null);
    localStorage.removeItem('fracadapt-token');
    localStorage.removeItem('fracadapt-refresh');
  }, [accessToken, refreshToken]);

  // Refresh the access token
  const refreshAccessToken = useCallback(async () => {
    if (!refreshToken) return false;
    try {
      const res = await fetch(`${API_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) {
        logout();
        return false;
      }
      const data = await res.json();
      setAccessToken(data.accessToken);
      localStorage.setItem('fracadapt-token', data.accessToken);
      return true;
    } catch {
      logout();
      return false;
    }
  }, [refreshToken, logout]);

  // Check if we have a valid session on mount
  useEffect(() => {
    const checkAuth = async () => {
      if (!accessToken) {
        setLoading(false);
        return;
      }
      try {
        const res = await fetch(`${API_URL}/api/auth/me`, {
          headers: { 'Authorization': `Bearer ${accessToken}` },
        });
        if (res.ok) {
          const data = await res.json();
          setUser(data.user);
        } else if (res.status === 401 && refreshToken) {
          // Try refreshing
          const refreshed = await refreshAccessToken();
          if (refreshed) {
            // Retry with new token
            const newToken = localStorage.getItem('fracadapt-token');
            const retryRes = await fetch(`${API_URL}/api/auth/me`, {
              headers: { 'Authorization': `Bearer ${newToken}` },
            });
            if (retryRes.ok) {
              const data = await retryRes.json();
              setUser(data.user);
            } else {
              logout();
            }
          }
        } else {
          logout();
        }
      } catch {
        logout();
      }
      setLoading(false);
    };
    checkAuth();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh token every 50 minutes (token expires in 60)
  useEffect(() => {
    if (!accessToken) return;
    const interval = setInterval(() => {
      refreshAccessToken();
    }, 50 * 60 * 1000);
    return () => clearInterval(interval);
  }, [accessToken, refreshAccessToken]);

  // Login
  const login = async (userId, password) => {
    const res = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, password }),
    });

    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.error || data.message || 'Login failed');
    }

    setAccessToken(data.accessToken);
    setRefreshToken(data.refreshToken);
    setUser(data.user);
    localStorage.setItem('fracadapt-token', data.accessToken);
    localStorage.setItem('fracadapt-refresh', data.refreshToken);
    return data;
  };

  return (
    <AuthContext.Provider value={{
      user,
      accessToken,
      isAuthenticated,
      loading,
      login,
      logout,
      getAuthHeaders,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
